import type { Color } from '@mtg-deck-tool/shared';
import { useSearchStore } from '../../stores/search-store.js';
import { useDeckStore } from '../../stores/deck-store.js';
import { useBarClause } from '../../hooks/useBarClause.js';
import { useSymbolMap } from '../../hooks/useSymbolMap.js';

const COLOR_OPTIONS: Array<{ value: Color | 'C'; symbol: string; label: string }> = [
  { value: 'W', symbol: '{W}', label: 'White' },
  { value: 'U', symbol: '{U}', label: 'Blue' },
  { value: 'B', symbol: '{B}', label: 'Black' },
  { value: 'R', symbol: '{R}', label: 'Red' },
  { value: 'G', symbol: '{G}', label: 'Green' },
  { value: 'C' as Color, symbol: '{C}', label: 'Colorless' },
];

const MODE_OPTIONS: Array<{ value: 'exactly' | 'including' | 'atMost'; label: string }> = [
  { value: 'exactly', label: 'Exactly' },
  { value: 'including', label: 'Including' },
  { value: 'atMost', label: 'At Most' },
];

const selectClass =
  'bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-orange-500 transition-colors cursor-pointer';

const MODE_TO_PREFIX: Record<string, string> = {
  exactly: 'c=',
  including: 'c>=',
  atMost: 'c<=',
};

const ALL_COLOR_PREFIXES = ['c=', 'c>=', 'c<='];

export function ColorsFilter() {
  const colors = useSearchStore((s) => s.advancedFilters.colors);
  const setAdvancedFilter = useSearchStore((s) => s.setAdvancedFilter);
  const colorIdentity = useDeckStore((s) => s.colorIdentity);
  const { writeClause } = useBarClause();
  const symbolMap = useSymbolMap();

  // Commander's color identity as a set (empty = no restriction)
  const identitySet = new Set(colorIdentity as string[]);
  const hasCommander = identitySet.size > 0;

  const syncBar = (selected: string[], mode: string) => {
    for (const p of ALL_COLOR_PREFIXES) writeClause(p, null);
    if (selected.length > 0) {
      writeClause(MODE_TO_PREFIX[mode]!, selected.join(''));
    }
  };

  const toggleColor = (c: Color | 'C') => {
    const selected = colors.selected as string[];
    const next = selected.includes(c)
      ? selected.filter((s) => s !== c)
      : [...selected, c];
    setAdvancedFilter('colors', { ...colors, selected: next as Color[] });
    syncBar(next, colors.mode);
  };

  const setMode = (mode: 'exactly' | 'including' | 'atMost') => {
    setAdvancedFilter('colors', { ...colors, mode });
    syncBar(colors.selected as string[], mode);
  };

  return (
    <div className="space-y-1.5">
      <label className="text-[10px] text-gray-500 uppercase tracking-wider">Colors</label>
      <div className="flex items-center gap-1.5">
        {COLOR_OPTIONS.map((opt) => {
          const isSelected = (colors.selected as string[]).includes(opt.value);
          // Disable colors outside commander's identity (C is always allowed)
          const isDisabled = hasCommander && opt.value !== 'C' && !identitySet.has(opt.value);
          const svgUri = symbolMap[opt.symbol];

          return (
            <button
              key={opt.value}
              onClick={() => !isDisabled && toggleColor(opt.value)}
              title={isDisabled ? `${opt.label} (not in commander identity)` : opt.label}
              disabled={isDisabled}
              className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors border ${
                isDisabled
                  ? 'bg-gray-900 border-gray-800 opacity-30 cursor-not-allowed'
                  : isSelected
                    ? 'bg-orange-600 border-orange-500'
                    : 'bg-gray-800 border-gray-700 hover:border-gray-500'
              }`}
            >
              {svgUri ? (
                <img src={svgUri} alt={opt.label} className="w-4 h-4" />
              ) : (
                <span className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-gray-500'}`}>
                  {opt.value}
                </span>
              )}
            </button>
          );
        })}
        <select
          value={colors.mode}
          onChange={(e) => setMode(e.target.value as 'exactly' | 'including' | 'atMost')}
          className={selectClass}
        >
          {MODE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
